import { ChatProvider, useChat } from "@/contexts/ChatContext";
import { useCanvasIntegration } from "@/hooks/useCanvasIntegration";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { useThreadStore } from "@/store";
import PropTypes from "prop-types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChatContent } from "./ChatContent";
import ChatLayout from "./ChatLayout";
import { DeepResearch } from "./DeepResearch";
import SourcesSidebar from "./messages/components/SourcesSidebar";
import PremiumToolsDialog from "./PremiumToolsDialog";
import ImageArtifactSidebar from "./sidebars/ImageArtifactSidebar";
import { StreamingNotification } from "./StreamingNotification";

/**
 * ChatInterface - Production version with optimized streaming support
 * Simplified version with performance optimizations and stable file references
 */
export function ChatInterface({
  onNewChat: _onNewChat,
  isLoading: _isLoading,
  isLimitedMode = false,
  disableTools = false,
  disableFileUpload = false,
  onShowAuthOverlay,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const prevPathRef = useRef(location.pathname);
  const expectingMessagesRef = useRef(false); // Declare early to avoid reference errors
  const lastClearRef = useRef(0); // Track when we last cleared to avoid excessive clearing
  const lastFetchedThreadRef = useRef(null); // Track last fetched thread to prevent duplicates
  const isFetchingRef = useRef(false); // Track if currently fetching to prevent concurrent fetches

  // CRITICAL FIX: Check if we're on /chat/new FIRST before reading params
  const isOnNewChatRoute = location.pathname === "/chat/new";

  // Get route params - use chatId or threadId (router uses :chatId)
  // Only read params if NOT on new chat route to avoid race conditions
  const { chatId, threadId, id } = useParams();
  const paramId = isOnNewChatRoute ? null : chatId || threadId || id; // Support multiple route param names

  // CRITICAL RACE CONDITION FIX: Clear state when on /chat/new using useLayoutEffect
  // This runs synchronously after render but before paint, avoiding setState-in-render warning
  useLayoutEffect(() => {
    if (isOnNewChatRoute) {
      const store = useThreadStore.getState();
      const now = Date.now();
      const shouldClear =
        (store.currentThreadId !== null ||
          (store.messages && store.messages.length > 0)) &&
        now - lastClearRef.current > 100; // Debounce clearing to max once per 100ms

      if (shouldClear) {
        // Clear immediately - this is safe because Zustand batches updates
        store.setCurrentThreadId(null);
        store.setMessages([]);
        lastClearRef.current = now;
        if (import.meta.env.DEV) {
          console.log(
            "[ChatInterface useLayoutEffect] Cleared state on /chat/new"
          );
        }
      }
    }
  }, [isOnNewChatRoute]);

  // Generate a stable key based on route path that changes only when thread changes
  const [routeKey, setRouteKey] = useState(
    `route-${location.pathname.replace(/\//g, "-")}`
  );

  // Simplified thread management with background streaming support
  // FAST THREAD SWITCHING: Clear messages and set loading immediately when thread changes
  useEffect(() => {
    // CRITICAL: Always check pathname first to prevent fetching thread on /chat/new
    if (isOnNewChatRoute) {
      // Early return - don't try to fetch thread when on /chat/new
      return;
    }

    if (paramId && paramId !== "new") {
      const {
        setCurrentThreadId,
        fetchThread,
        getStreamForThread,
        threadMessages,
        setMessages,
        setThreadStoreLoading,
        currentThreadId,
      } = useThreadStore.getState();

      // Prevent duplicate fetches - skip if already fetching or just fetched this thread
      if (isFetchingRef.current || lastFetchedThreadRef.current === paramId) {
        return;
      }

      // Mark as fetching and record which thread
      isFetchingRef.current = true;
      lastFetchedThreadRef.current = paramId;

      // FAST THREAD SWITCHING: Clear messages immediately (synchronously) when thread changes
      // This makes the UI feel instant and responsive
      if (paramId !== currentThreadId) {
        setMessages([]);
        setThreadStoreLoading(true);
      }

      fetchThread(paramId)
        .then((thread) => {
          if (thread) {
            setCurrentThreadId(paramId);

            // BACKGROUND STREAMING: Check if thread has active stream or stored messages
            const activeStream = getStreamForThread(paramId);
            const storedMessages = threadMessages?.get(paramId);

            // UPDATED LOGIC: Only use cached messages if stream is ACTIVE
            // If stream completed in background, fetch from database to get complete messages
            if (activeStream && storedMessages && storedMessages.length > 0) {
              // Stream is active - use cached messages that are being updated in real-time
              setMessages(storedMessages);
              setThreadStoreLoading(false);
              isFetchingRef.current = false; // Clear fetching flag
            } else {
              // CRITICAL FIX: If this is an optimistic thread (being created), don't fetch from DB yet
              // Just wait for messages to be added by the streaming flow
              if (thread.metadata?.isOptimistic) {
                console.log(
                  `[ChatInterface] Thread ${paramId} is optimistic, waiting for messages from streaming`
                );
                setThreadStoreLoading(false);
                isFetchingRef.current = false;
                return;
              }

              // No active stream - fetch from database to get complete messages
              // This handles: new navigation to thread, background stream completed, or no cached messages
              const { getThreadMessages } = useThreadStore.getState();
              getThreadMessages(paramId, { forceRefresh: false })
                .then((_loadedMessages) => {
                  // Messages are set by getThreadMessages, loading state is also managed there
                  isFetchingRef.current = false; // Clear fetching flag
                })
                .catch((loadError) => {
                  console.error(
                    `[ChatInterface] Error loading messages for thread ${paramId}:`,
                    loadError
                  );
                  setThreadStoreLoading(false);
                  isFetchingRef.current = false; // Clear fetching flag on error
                });
            }
          } else {
            // CRITICAL FIX: Before showing "chat not found", check if this might be an optimistic thread
            // that hasn't been added to the cache yet due to React render timing
            const { currentThreadId: storeThreadId, optimisticMessages } =
              useThreadStore.getState();
            const hasOptimisticMessages = optimisticMessages?.has(paramId);

            // If the current thread ID matches and we have optimistic messages, don't redirect
            if (storeThreadId === paramId || hasOptimisticMessages) {
              console.log(
                `[ChatInterface] Thread ${paramId} not found in DB but appears to be optimistic, waiting...`
              );
              setThreadStoreLoading(false);
              isFetchingRef.current = false;
              return;
            }

            // Thread doesn't exist, redirect to new chat with user feedback
            console.log(
              `[ChatInterface] Thread ${paramId} not found, redirecting to /chat/new`
            );
            toast({
              title: "Chat not found",
              description: "This chat may have been deleted or doesn't exist.",
              variant: "destructive",
            });
            setThreadStoreLoading(false);
            isFetchingRef.current = false; // Clear fetching flag
            navigate("/chat/new", { replace: true });
          }
        })
        .catch((error) => {
          console.error(`[ChatInterface] Error fetching thread:`, error);
          toast({
            title: "Error loading chat",
            description: "Unable to load this chat. Please try again.",
            variant: "destructive",
          });
          setThreadStoreLoading(false);
          isFetchingRef.current = false; // Clear fetching flag on error
          // On error, redirect to new chat
          navigate("/chat/new", { replace: true });
        });
    }
  }, [paramId, isOnNewChatRoute, navigate, toast]);

  // Simplified path change handling
  useEffect(() => {
    const isNewPath = location.pathname === "/chat/new";
    const isPathChanged = prevPathRef.current !== location.pathname;

    if (isPathChanged) {
      prevPathRef.current = location.pathname;
      setRouteKey(`route-${location.pathname.replace(/\//g, "-")}`);

      if (isNewPath) {
        // CRITICAL FIX: Reset expectingMessagesRef immediately when navigating back to /chat/new
        // This prevents the grace period from allowing stale messages to persist
        expectingMessagesRef.current = false;
        lastFetchedThreadRef.current = null; // Reset to allow fetching threads again
        isFetchingRef.current = false; // Reset fetching flag

        const { clearCurrentThread, setMessages, setCurrentThreadId } =
          useThreadStore.getState();

        // CRITICAL FIX: Always clear thread and messages when navigating to /chat/new
        // This ensures background streams don't show in the new thread view
        clearCurrentThread();
        setCurrentThreadId(null); // Explicitly set to null to prevent background updates
        setMessages([]);

        if (import.meta.env.DEV) {
          console.log(
            "[ChatInterface] Cleared thread and set currentThreadId to null for /chat/new"
          );
        }
      }
    }
  }, [location.pathname]);

  // Listen for new chat started events to reset state even when already on /chat/new
  useEffect(() => {
    const handleNewChatStarted = () => {
      if (location.pathname === "/chat/new") {
        console.log(
          "[ChatInterface] Resetting state for new chat on /chat/new"
        );

        // Clear thread and messages
        const { clearCurrentThread, setMessages, setCurrentThreadId } =
          useThreadStore.getState();
        clearCurrentThread();
        setCurrentThreadId(null);
        setMessages([]);

        // Reset local state
        setRouteKey(`route-chat-new-${Date.now()}`);
      }
    };

    window.addEventListener("new-chat-started", handleNewChatStarted);

    return () => {
      window.removeEventListener("new-chat-started", handleNewChatStarted);
    };
  }, [location.pathname]);

  // CRITICAL FIX: Ensure messages stay empty when on /chat/new
  // This prevents background stream completions from loading messages
  // BUT: Allow messages to load when navigating to a thread (threadId is set before pathname changes)
  const currentThreadId = useThreadStore((state) => state.currentThreadId);
  const messages = useThreadStore((state) => state.messages);

  // CRITICAL FIX: Use layoutEffect to synchronously clear stale messages BEFORE render
  // This catches race conditions where messages load after navigation starts but before pathname updates
  useLayoutEffect(() => {
    const isNewPath = location.pathname === "/chat/new";

    // If we're on /chat/new and not expecting messages, ensure everything is cleared
    if (isNewPath && !expectingMessagesRef.current) {
      const {
        setMessages,
        setCurrentThreadId,
        currentThreadId: storeThreadId,
        messages: storeMessages,
      } = useThreadStore.getState();

      // Check both the zustand selector state and the store state directly
      if (
        currentThreadId ||
        storeThreadId ||
        (messages && messages.length > 0) ||
        (storeMessages && storeMessages.length > 0)
      ) {
        setCurrentThreadId(null);
        setMessages([]);
        if (import.meta.env.DEV) {
          console.log(
            "[ChatInterface layoutEffect] Synchronously cleared stale messages on /chat/new",
            {
              selectorThreadId: currentThreadId,
              storeThreadId,
              selectorMessageCount: messages?.length || 0,
              storeMessageCount: storeMessages?.length || 0,
            }
          );
        }
      }
    }
  }, [location.pathname, currentThreadId, messages]);

  // Track when we're expecting messages (threadId set while on /chat/new indicates navigation starting)
  useEffect(() => {
    const isNewPath = location.pathname === "/chat/new";

    if (isNewPath && currentThreadId) {
      // We're on /chat/new but have a threadId - this means navigation is starting
      // Set flag to allow messages to load
      expectingMessagesRef.current = true;
      if (import.meta.env.DEV) {
        console.log(
          "[ChatInterface] Expecting messages for thread:",
          currentThreadId
        );
      }
    } else if (!isNewPath) {
      // Once we leave /chat/new, reset the flag
      expectingMessagesRef.current = false;
    }
  }, [location.pathname, currentThreadId]);

  // Separate effect: Clear messages on /chat/new, but NOT when we're expecting them for navigation
  useEffect(() => {
    const isNewPath = location.pathname === "/chat/new";

    // Don't clear if we're expecting messages (navigation in progress)
    if (expectingMessagesRef.current) {
      return;
    }

    if (isNewPath) {
      const {
        setMessages,
        setCurrentThreadId,
        currentThreadId: storeThreadId,
      } = useThreadStore.getState();

      // If we're on /chat/new but have messages or a thread ID, clear them
      // This catches cases where background streams complete and set messages
      // CRITICAL: Also check the store directly to catch race conditions
      if (
        currentThreadId ||
        storeThreadId ||
        (messages && messages.length > 0)
      ) {
        setCurrentThreadId(null);
        setMessages([]);
        if (import.meta.env.DEV) {
          console.log(
            "[ChatInterface] Cleared messages and threadId on /chat/new - background stream may have completed",
            {
              hadThreadId: !!currentThreadId,
              storeThreadId,
              messageCount: messages?.length || 0,
            }
          );
        }
      }
    }
  }, [location.pathname, currentThreadId, messages]);

  return (
    <ChatProvider>
      <div
        key={routeKey}
        className="relative h-full w-full overflow-hidden flex flex-col"
      >
        <StreamingNotification />
        <ChatInterfaceContent>
          <ChatLayout>
            <ChatContent
              isLimitedMode={isLimitedMode}
              disableTools={disableTools}
              disableFileUpload={disableFileUpload}
              onShowAuthOverlay={onShowAuthOverlay}
            />
          </ChatLayout>
        </ChatInterfaceContent>
      </div>
    </ChatProvider>
  );
}

/**
 * Inner content with sidebars - simplified from original
 */
function ChatInterfaceContent({ children }) {
  const location = useLocation();
  const {
    deepResearchSidebarOpen,
    setDeepResearchSidebarOpen,
    activeDeepResearch,
    sourcesSidebarOpen,
    setSourcesSidebarOpen,
    activeSources,
    setActiveDeepResearch: _setActiveDeepResearch,
    imageArtifactSidebarOpen,
    setImageArtifactSidebarOpen,
    activeImageArtifact,
    clearThreadLock,
  } = useChat();

  // Initialize canvas integration
  useCanvasIntegration();

  // CLEANUP: Clear thread lock when navigating between routes
  useEffect(() => {
    if (clearThreadLock) {
      clearThreadLock();
    }
  }, [location.pathname, clearThreadLock]);

  // Listen for canvas open events to close sidebars
  useEffect(() => {
    const handleCloseSidebars = () => {
      setDeepResearchSidebarOpen(false);
      setSourcesSidebarOpen(false);
      setImageArtifactSidebarOpen(false);
    };

    window.addEventListener("close-all-sidebars", handleCloseSidebars);

    return () => {
      window.removeEventListener("close-all-sidebars", handleCloseSidebars);
    };
  }, [
    setDeepResearchSidebarOpen,
    setSourcesSidebarOpen,
    setImageArtifactSidebarOpen,
  ]);

  // Calculate margin for sidebars
  const getMainContentMargin = () => {
    const openSidebars = [
      deepResearchSidebarOpen,
      sourcesSidebarOpen,
      imageArtifactSidebarOpen,
    ].filter(Boolean).length;

    if (openSidebars === 3) {
      return "mr-[1216px]";
    } else if (openSidebars === 2) {
      if (imageArtifactSidebarOpen) {
        return "mr-[832px]";
      } else {
        return "mr-[768px]";
      }
    } else if (openSidebars === 1) {
      if (imageArtifactSidebarOpen) {
        return "mr-[448px]";
      } else {
        return "mr-96";
      }
    }
    return "mr-0";
  };

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Main Content Area */}
      <div
        className={cn(
          "flex-1 h-full transition-all duration-500 ease-in-out",
          getMainContentMargin()
        )}
      >
        {children}
      </div>

      {/* Sources Sidebar */}
      <div
        className={cn(
          "fixed right-0 top-0 h-full w-96 bg-background shadow-xl transform transition-transform duration-500 ease-in-out z-[9998]",
          sourcesSidebarOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {sourcesSidebarOpen && activeSources && (
          <SourcesSidebar
            isOpen={true}
            onClose={() => setSourcesSidebarOpen(false)}
            sources={activeSources}
            title="Citations"
          />
        )}
      </div>

      {/* Deep Research Sidebar */}
      <div
        className={cn(
          "fixed top-0 h-full w-96 bg-background shadow-xl transform transition-transform duration-500 ease-in-out z-[9999]",
          deepResearchSidebarOpen && sourcesSidebarOpen
            ? "right-96 translate-x-0"
            : deepResearchSidebarOpen
              ? "right-0 translate-x-0"
              : "right-0 translate-x-full"
        )}
      >
        {deepResearchSidebarOpen && activeDeepResearch && (
          <DeepResearch
            isActive={true}
            onClose={() => setDeepResearchSidebarOpen(false)}
            activity={activeDeepResearch.activity || []}
            sources={activeDeepResearch.sources || []}
            isLoading={activeDeepResearch.status === "active"}
            status={activeDeepResearch.status || "active"}
            deepResearch={true}
            messageId={activeDeepResearch.messageId}
            originalQueryMessageId={activeDeepResearch.originalQueryMessageId}
          />
        )}
      </div>

      {/* Image Artifact Sidebar */}
      <div
        className={cn(
          "fixed top-0 h-full w-[448px] bg-background shadow-xl transform transition-transform duration-500 ease-in-out z-[9997]",
          (() => {
            const openSidebars = [
              sourcesSidebarOpen,
              deepResearchSidebarOpen,
            ].filter(Boolean).length;
            if (imageArtifactSidebarOpen && openSidebars === 2) {
              return "right-[768px] translate-x-0";
            } else if (imageArtifactSidebarOpen && openSidebars === 1) {
              return "right-96 translate-x-0";
            } else if (imageArtifactSidebarOpen && openSidebars === 0) {
              return "right-0 translate-x-0";
            }
            return "right-0 translate-x-full";
          })()
        )}
      >
        {imageArtifactSidebarOpen && activeImageArtifact && (
          <ImageArtifactSidebar
            open={true}
            onClose={() => setImageArtifactSidebarOpen(false)}
            imageData={activeImageArtifact}
          />
        )}
      </div>

      <PremiumToolsDialog />
    </div>
  );
}

ChatInterface.propTypes = {
  onNewChat: PropTypes.func,
  isLoading: PropTypes.bool,
  isLimitedMode: PropTypes.bool,
  disableTools: PropTypes.bool,
  disableFileUpload: PropTypes.bool,
  onShowAuthOverlay: PropTypes.func,
};

ChatInterfaceContent.propTypes = {
  children: PropTypes.node,
};

export default ChatInterface;
